/**
 * Handles chunk loading errors gracefully by reloading the page once.
 * This happens when a new deployment changes asset filenames while
 * the user has an old cached version of index.html.
 *
 * Best practice approach used by Vercel, Netlify, and other production SPAs.
 */

import { errorTracker } from '@/common/error-tracking';

const CHUNK_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'importing a module script failed',
  'error loading dynamically imported module',
];

const RELOAD_FLAG_KEY = 'chunk-error-reloaded';

/**
 * Checks if an error is a chunk loading error
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  const message = error.message.toLowerCase();

  return CHUNK_ERROR_PATTERNS.some(pattern => message.includes(pattern));
}

/**
 * Handles chunk loading errors by reloading the page once.
 * Logs to error tracking service for monitoring.
 */
export function handleChunkError(error: unknown): void {
  if (!isChunkLoadError(error)) {
    return;
  }

  const hasReloaded = sessionStorage.getItem(RELOAD_FLAG_KEY);

  if (!hasReloaded) {
    // Log before reload for monitoring
    errorTracker.captureError(error as Error, {
      level: 'warning',
      tags: {
        type: 'chunk-load-recovery',
        action: 'auto-reload',
      },
      extra: {
        errorMessage: (error as Error).message,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      },
    });

    // Mark that we're reloading to prevent infinite loops
    sessionStorage.setItem(RELOAD_FLAG_KEY, 'true');

    console.warn(
      'Chunk load error detected. Reloading to fetch fresh assets...'
    );

    // Reload the page to get fresh assets
    window.location.reload();
  } else {
    // Reload didn't help - this is a real error
    sessionStorage.removeItem(RELOAD_FLAG_KEY);

    // Log persistent error
    errorTracker.captureError(error as Error, {
      level: 'error',
      tags: {
        type: 'chunk-load-error',
        action: 'reload-failed',
      },
      extra: {
        errorMessage: (error as Error).message,
        reloadAttempted: true,
      },
    });

    console.error('Chunk load error persists after reload:', error);
  }
}

/**
 * Clears the reload flag on successful app load.
 * Call this in your app's initialization.
 */
export function clearReloadFlag(): void {
  sessionStorage.removeItem(RELOAD_FLAG_KEY);
}
